import { createBrowserRouter, Navigate, useParams } from 'react-router';

import { AppShell } from '@/widgets/app-shell/app-shell';

/**
 * The Broccoli route map. Every route here is backed by a screen that talks to the real API —
 * an area that is not built yet is absent rather than stubbed, so the nav never promises
 * something that does not answer.
 *
 * Cost and schedule live under `jobs/:jobId` rather than at the top level. A cost table with
 * no job title above it reads as somebody else's numbers, and the tab strip is how a person
 * gets from the money to the programme without going back to a list first. The flat paths
 * are kept as redirects so a link written before this holds up.
 */
function RedirectToJobTab({ tab }: { tab: string }) {
  const { jobId = '' } = useParams();
  return <Navigate to={`/jobs/${jobId}/${tab}`} replace />;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    lazy: async () => ({ Component: (await import('@/pages/login-page')).LoginPage }),
  },
  {
    path: '/',
    element: <AppShell />,
    children: [
      {
        index: true,
        lazy: async () => ({ Component: (await import('@/pages/dashboard-page')).DashboardPage }),
      },
      {
        path: 'pipeline',
        lazy: async () => ({ Component: (await import('@/pages/pipeline-page')).PipelinePage }),
      },
      {
        path: 'jobs',
        lazy: async () => ({ Component: (await import('@/pages/jobs-page')).JobsPage }),
      },
      {
        /* Registered before `jobs/:jobId` so "new" is a page, not a job id. */
        path: 'jobs/new',
        lazy: async () => ({ Component: (await import('@/pages/job-new-page')).JobNewPage }),
      },
      {
        path: 'jobs/:jobId',
        lazy: async () => ({
          Component: (await import('@/pages/job-detail-page')).JobDetailPage,
        }),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('@/pages/job-overview-tab')).JobOverviewTab,
            }),
          },
          {
            path: 'documents',
            lazy: async () => ({
              Component: (await import('@/pages/job-documents-tab')).JobDocumentsTab,
            }),
          },
          {
            path: 'money',
            lazy: async () => ({
              Component: (await import('@/pages/job-money-tab')).JobMoneyTab,
            }),
          },
          {
            path: 'budget',
            lazy: async () => ({ Component: (await import('@/pages/budget-page')).BudgetPage }),
          },
          {
            path: 'schedule',
            lazy: async () => ({
              Component: (await import('@/pages/schedule-page')).SchedulePage,
            }),
          },
          {
            path: 'photos',
            lazy: async () => ({
              Component: (await import('@/pages/job-photos-tab')).JobPhotosTab,
            }),
          },
          {
            path: 'inspections',
            lazy: async () => ({
              Component: (await import('@/pages/job-inspections-tab')).JobInspectionsTab,
            }),
          },
          {
            path: 'approvals',
            lazy: async () => ({
              Component: (await import('@/pages/approvals')).JobApprovalsTab,
            }),
          },
        ],
      },

      { path: 'budget/:jobId', element: <RedirectToJobTab tab="budget" /> },
      { path: 'schedule/:jobId', element: <RedirectToJobTab tab="schedule" /> },

      {
        path: 'approvals',
        lazy: async () => ({ Component: (await import('@/pages/approvals')).ApprovalsPage }),
      },
      {
        path: 'procurement',
        lazy: async () => ({
          Component: (await import('@/pages/procurement-page')).ProcurementPage,
        }),
      },
      {
        path: 'site-report',
        lazy: async () => ({
          Component: (await import('@/pages/site-report-page')).SiteReportPage,
        }),
      },
      {
        path: 'reports',
        lazy: async () => ({ Component: (await import('@/pages/reports-page')).ReportsPage }),
      },
      {
        path: 'reports/workload',
        lazy: async () => ({ Component: (await import('@/pages/workload-page')).WorkloadPage }),
      },
      {
        path: 'admin/stages',
        lazy: async () => ({ Component: (await import('@/pages/stages-page')).StagesPage }),
      },
      {
        path: 'admin/roles',
        lazy: async () => ({ Component: (await import('@/pages/roles-page')).RolesPage }),
      },

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
